import { MutableRefObject, useCallback } from "react";
import { CRUD_CREATE, CRUD_UPDATE, OnFailure, OnSuccess, Record, SetOnFailure, SetOnSuccess, SetTransformData, TransformData, useCheckMinimumRequiredProps, useCreate, useGetResourceLabel, useNotify, useRefresh, useSaveModifiers, useTranslate, useUpdate, useVersion } from "react-admin";

export interface DialogControllerProps<
    RecordType extends Omit<Record, 'id'> = Record
    > {
    basePath?: string;
    defaultTitle: string;
    onSuccessRef: MutableRefObject<OnSuccess>;
    onFailureRef: MutableRefObject<OnFailure>;
    transformRef: MutableRefObject<TransformData>;
    save: (
        record: Partial<Record>,
        callbacks?: {
            transform?: TransformData;
        }
    ) => void;
    saving: boolean,
    setOnSuccess: SetOnSuccess;
    setOnFailure: SetOnFailure;
    setTransform: SetTransformData;
    successMessage?: string;
    record?: Partial<RecordType>;
    resource: string;
    version: number;
}

export const useDialogController = <RecordType extends Record = Record>(
    props
): DialogControllerProps<RecordType> => {
    useCheckMinimumRequiredProps('Dialog', ['resource'], props);
    const {
        resource,
        basePath = `/${resource}`,
        onSuccess,
        onFailure,
        transform,
        record = {},
        id = record && record.id ? `${record.id}` : undefined,
        mutationMode = "pessimistic",
        handleClose,
        name
    } = props;

    const translate = useTranslate();
    const notify = useNotify();
    const refresh = useRefresh();
    const version = useVersion();

    const {
        onSuccessRef,
        setOnSuccess,
        onFailureRef,
        setOnFailure,
        transformRef,
        setTransform,
    } = useSaveModifiers({ onSuccess, onFailure, transform });

    const getResourceLabel = useGetResourceLabel();

    const defaultTitle = (id ? `${translate("resources.editTitle")} ` : "") + translate(`ra.page.${id ? "edit" : "create"}`, {
        name: name || getResourceLabel(resource, 1),
        id,
        record,
    });
    const [create, { loading: savingCreate }] = useCreate(resource);
    const [update, { loading: savingUpdate }] = useUpdate(
        resource,
        id,
        {},
        record
    );
    const saving = id ? savingUpdate : savingCreate;
    const actionFn = id ? update : create;
    const save = useCallback(
        (
            data: Partial<Record>,
            { transform } = { transform: null }
        ) =>
            Promise.resolve(
                transform
                    ? transform(data)
                    : transformRef.current
                        ? transformRef.current(data)
                        : data
            ).then(data =>
                actionFn(
                    { payload: { data } },
                    {
                        action: id ? CRUD_UPDATE : CRUD_CREATE,
                        onSuccess: onSuccessRef.current
                            ? onSuccessRef.current
                            : () => {
                                notify(
                                    `ra.notification.${id ? "updated" : "created"}`,
                                    {
                                        type: 'info',
                                        messageArgs: { smart_count: 1 },
                                        undoable: mutationMode === 'undoable',
                                    }
                                );
                                handleClose();
                            },
                        onFailure: onFailureRef.current
                            ? onFailureRef.current
                            : error => {
                                notify(
                                    typeof error === 'string'
                                        ? error
                                        : error.message ||
                                        'ra.notification.http_error',
                                    {
                                        type: 'warning',
                                        messageArgs: {
                                            _:
                                                typeof error === 'string'
                                                    ? error
                                                    : error && error.message
                                                        ? error.message
                                                        : undefined,
                                        },
                                    }
                                );
                                if (
                                    mutationMode === 'undoable' ||
                                    mutationMode === 'pessimistic'
                                ) {
                                    refresh();
                                }
                            },
                        mutationMode,
                    }
                )
            ),
        [
            transformRef,
            actionFn,
            onSuccessRef,
            onFailureRef,
            notify,
            refresh,
            mutationMode,
            handleClose,
            id
        ]
    );

    return {
        defaultTitle,
        onSuccessRef,
        onFailureRef,
        transformRef,
        save,
        saving,
        setOnSuccess,
        setOnFailure,
        setTransform,
        resource,
        basePath,
        record,
        version
    };
};